"use client";
import type { SanctuarySyncResponse } from "@9amhealth/cms-api";
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Inline,
  Stack,
  Text
} from "@sanity/ui";
import { FC, useState } from "react";
import { defineType, useClient } from "sanity";
import {
  buildSanctuaryVideoDoc,
  buildSyncPlan,
  collectExistingVimeoIds,
  EXISTING_VIDEOS_QUERY,
  type ChangedVideo,
  type ExistingSanctuaryVideoDoc,
  type PreparedSanctuaryVideoDoc,
  type SyncPlan
} from "./syncVideosSanctuaryHealthHelpers";

// Same-origin: CloudFront routes /api/* to the CMS lambda in deployed
// environments; the vite dev server proxies it locally (see vite.config.ts).
const SANCTUARY_SYNC_ENDPOINT = "/api/content/sanctuary-sync";

const Input: FC = () => {
  return <></>;
};

const PlanSummary: FC<{ plan: SyncPlan }> = ({ plan }) => (
  <Inline gap={2}>
    <Badge tone="positive">{plan.added.length} added</Badge>
    <Badge tone="caution">{plan.changed.length} changed</Badge>
    <Badge tone="critical">{plan.removed.length} removed</Badge>
    <Badge>{plan.unchangedCount} unchanged</Badge>
  </Inline>
);

const SectionHeading: FC<{ label: string }> = ({ label }) => (
  <Text size={1} weight="semibold">
    {label}
  </Text>
);

const AddedVideoCard: FC<{ doc: PreparedSanctuaryVideoDoc }> = ({ doc }) => (
  <Card padding={3} radius={2} border tone="positive">
    <Flex align="center" gap={2}>
      <Box flex={1}>
        <Text size={1} weight="medium" textOverflow="ellipsis">
          {doc.title || "(untitled)"}
        </Text>
      </Box>
      <Badge>{doc.videoLanguage}</Badge>
    </Flex>
  </Card>
);

/**
 * Diff of one field: name plus previous/next values on separate lines. Each
 * line is clamped by CSS ellipsis but keeps the full value copyable.
 */
const FieldChangeRow: FC<{
  field: string;
  previous: string;
  next: string;
}> = ({ field, previous, next }) => (
  <Stack gap={1}>
    <Text size={0} weight="medium">
      {field}
    </Text>
    <Text size={0} muted textOverflow="ellipsis">
      − {previous}
    </Text>
    <Text size={0} textOverflow="ellipsis">
      + {next}
    </Text>
  </Stack>
);

const ChangedVideoCard: FC<{ changed: ChangedVideo }> = ({ changed }) => {
  const { doc, changes } = changed;

  return (
    <Card padding={3} radius={2} border tone="caution">
      <Stack gap={3}>
        <Flex align="center" gap={2}>
          <Box flex={1}>
            <Text size={1} weight="medium" textOverflow="ellipsis">
              {doc.title || doc._id}
            </Text>
          </Box>
          <Badge>{doc.videoLanguage}</Badge>
          <Badge mode="outline">
            {changes.length} field{changes.length === 1 ? "" : "s"}
          </Badge>
        </Flex>
        <Stack gap={3}>
          {changes.map((change) => (
            <FieldChangeRow key={change.field} {...change} />
          ))}
        </Stack>
      </Stack>
    </Card>
  );
};

const RemovedVideoCard: FC<{ doc: ExistingSanctuaryVideoDoc }> = ({ doc }) => (
  <Card padding={3} radius={2} border tone="critical">
    <Text size={1} textOverflow="ellipsis">
      {doc.title ?? doc._id}
    </Text>
  </Card>
);

const SyncPlanPreview: FC<{ plan: SyncPlan }> = ({ plan }) => {
  const hasAnyDifference =
    plan.added.length > 0 || plan.changed.length > 0 || plan.removed.length > 0;

  return (
    <Stack gap={4}>
      <PlanSummary plan={plan} />
      {!hasAnyDifference && (
        <Card padding={3} radius={2} border tone="positive">
          <Text size={1}>Everything is up to date.</Text>
        </Card>
      )}
      {plan.added.length > 0 && (
        <Stack gap={2}>
          <SectionHeading label={`Added (${plan.added.length})`} />
          {plan.added.map((doc) => (
            <AddedVideoCard key={doc._id} doc={doc} />
          ))}
        </Stack>
      )}
      {plan.changed.length > 0 && (
        <Stack gap={2}>
          <SectionHeading label={`Changed (${plan.changed.length})`} />
          {plan.changed.map((changed) => (
            <ChangedVideoCard key={changed.doc._id} changed={changed} />
          ))}
        </Stack>
      )}
      {plan.removed.length > 0 && (
        <Stack gap={2}>
          <SectionHeading
            label={`Removed on Sanctuary Health (${plan.removed.length})`}
          />
          <Text size={0} muted>
            These no longer exist on Sanctuary Health but are kept in Sanity.
          </Text>
          {plan.removed.map((doc) => (
            <RemovedVideoCard key={doc._id} doc={doc} />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

const Field: FC = () => {
  "use client";

  const sanityClient = useClient();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string>("");
  const [plan, setPlan] = useState<SyncPlan | null>(null);

  const loadPreview = async () => {
    setLoading(true);
    setPlan(null);
    setStatus("Loading videos from Sanctuary Health...");
    try {
      const response = await fetch(SANCTUARY_SYNC_ENDPOINT);
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as {
          error?: string;
        } | null;
        throw new Error(body?.error ?? `Request failed (${response.status})`);
      }
      const res = (await response.json()) as SanctuarySyncResponse;

      setStatus("Comparing with current Sanity documents...");
      const existing = await sanityClient.fetch<ExistingSanctuaryVideoDoc[]>(
        EXISTING_VIDEOS_QUERY
      );
      const existingVimeoIds = collectExistingVimeoIds(existing);
      const prepared = res.videos
        .map((video) => buildSanctuaryVideoDoc(video, existingVimeoIds))
        .filter((doc): doc is PreparedSanctuaryVideoDoc => doc !== null);

      setPlan(buildSyncPlan(prepared, existing));
      setStatus(`Preview ready (${res.videos.length} videos loaded)`);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      setStatus(`Failed to prepare sync preview: ${message}`);
      console.error("Sanctuary Health sync preview failed", error);
    } finally {
      setLoading(false);
    }
  };

  const applyChanges = async () => {
    if (!plan) {
      return;
    }
    const docsToWrite = [...plan.added, ...plan.changed.map(({ doc }) => doc)];
    setLoading(true);
    setStatus(`Writing videos to Sanity... (${docsToWrite.length} documents)`);
    try {
      const transaction = sanityClient.transaction();
      for (const doc of docsToWrite) {
        transaction.createOrReplace(doc);
      }
      const res = await transaction.commit();
      setPlan(null);
      setStatus(`Applied ${res.results.length} changes.`);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      setStatus(`Error applying changes: ${message}`);
      console.error("Sanctuary Health sync failed", error);
    } finally {
      setLoading(false);
    }
  };

  const discardPreview = () => {
    setPlan(null);
    setStatus("");
  };

  const pendingWriteCount = plan ? plan.added.length + plan.changed.length : 0;

  return (
    <Stack gap={4}>
      {status && (
        <Text size={1} muted>
          {status}
        </Text>
      )}
      {plan && <SyncPlanPreview plan={plan} />}
      {!plan && (
        <Flex>
          <Button
            text="Sync Videos"
            tone="primary"
            loading={loading}
            disabled={loading}
            onClick={() => void loadPreview()}
          />
        </Flex>
      )}
      {plan && pendingWriteCount > 0 && (
        <Flex gap={2}>
          <Button
            text={`Apply ${pendingWriteCount} change${pendingWriteCount === 1 ? "" : "s"}`}
            tone="positive"
            loading={loading}
            disabled={loading}
            onClick={() => void applyChanges()}
          />
          <Button
            text="Discard"
            mode="ghost"
            disabled={loading}
            onClick={discardPreview}
          />
        </Flex>
      )}
      {plan && pendingWriteCount === 0 && (
        <Flex>
          <Button text="Close preview" mode="ghost" onClick={discardPreview} />
        </Flex>
      )}
    </Stack>
  );
};

export const syncVideosSanctuaryHealthType = defineType({
  type: "object",
  name: "syncVideosSanctuaryHealth",
  title: "Sync Videos with Sanctuary Health",
  fields: [
    {
      name: "syncVideos",
      type: "boolean",
      title: "Sync Videos with Sanctuary Health",
      components: {
        input: Input,
        field: Field
      }
    }
  ]
});
